import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  LayoutDashboard, Package, History, Settings, CheckCircle, Navigation,
  User, Mail, Phone, Building, MapPin, Save, Shield, Key, Bell,
  Globe, CreditCard, AlertCircle, CheckCircle as CheckIcon
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch-simple';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import { useClientStats } from '@/hooks/useClientStats';
import { getClientSidebarLinks } from '@/config/clientSidebarLinks';
import { authAPI } from '@/services/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import LogiConnectIdBadge from '@/components/ui/logiconnect-id-badge';
import useAuthStore from '@/store/authStore';

const ClientSettings = () => {
  const { stats } = useClientStats();
  const { user, updateUser } = useAuthStore();
  const [activeTab, setActiveTab] = useState('profile');
  
  const [profileData, setProfileData] = useState({
    company_name: user?.profile_data?.company_name || '',
    is_company: user?.profile_data?.is_company || false,
    domaine_activites: user?.profile_data?.domaine_activites || [],
    tax_info: user?.profile_data?.tax_info || '',
    address: user?.profile_data?.address || '',
    city: user?.profile_data?.city || '',
    country: 'Guinée',
  });

  const [userData, setUserData] = useState({
    first_name: user?.first_name || '',
    last_name: user?.last_name || '',
    email: user?.email || '',
    phone_number: user?.phone_number || '',
  });

  const [preferences, setPreferences] = useState({
    notifications_email: true,
    notifications_sms: false,
    notifications_push: true,
    language: 'fr',
    timezone: 'Africa/Conakry',
  });

  const [securityData, setSecurityData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  
  const sidebarLinks = getClientSidebarLinks();

  const handleSaveProfile = async () => {
    try {
      // TODO: Appeler l'API pour mettre à jour le profil
      toast({
        title: 'Profil mis à jour',
        description: 'Vos informations de profil ont été sauvegardées.',
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la mise à jour du profil.',
        variant: 'destructive',
      });
    }
  };

  const handleSaveUser = async () => {
    try {
      await updateUser(userData);
      toast({
        title: 'Informations mises à jour',
        description: 'Vos informations personnelles ont été sauvegardées.',
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la mise à jour des informations.',
        variant: 'destructive',
      });
    }
  };

  const handleSavePreferences = async () => {
    try {
      // TODO: Appeler l'API pour sauvegarder les préférences
      toast({
        title: 'Préférences sauvegardées',
        description: 'Vos préférences ont été mises à jour.',
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la sauvegarde des préférences.',
        variant: 'destructive',
      });
    }
  };

  const handleChangePassword = async () => {
    if (securityData.new_password !== securityData.confirm_password) {
      toast({
        title: 'Erreur',
        description: 'Les mots de passe ne correspondent pas.',
        variant: 'destructive',
      });
      return;
    }

    if (securityData.new_password.length < 8) {
      toast({
        title: 'Erreur',
        description: 'Le mot de passe doit contenir au moins 8 caractères.',
        variant: 'destructive',
      });
      return;
    }

    try {
      // TODO: Appeler l'API pour changer le mot de passe
      toast({
        title: 'Mot de passe changé',
        description: 'Votre mot de passe a été modifié avec succès.',
      });
      setSecurityData({
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors du changement de mot de passe.',
        variant: 'destructive',
      });
    }
  };

  return (
    <>
      <Helmet>
        <title>Paramètres - LogiConnect</title>
      </Helmet>
      <DashboardLayout sidebarLinks={sidebarLinks}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Paramètres</h1>
            <p className="text-muted-foreground mt-2">Gérez votre profil et vos préférences</p>
            {(user?.logiconnect_id || user?.profile_data?.logiconnect_id) && (
              <div className="mt-2">
                <LogiConnectIdBadge 
                  id={user?.profile_data?.logiconnect_id || user?.logiconnect_id} 
                  showLabel={true}
                  size="sm"
                />
              </div>
            )}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="profile">Profil</TabsTrigger>
            <TabsTrigger value="account">Compte</TabsTrigger>
            <TabsTrigger value="preferences">Préférences</TabsTrigger>
            <TabsTrigger value="security">Sécurité</TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle>Informations du profil</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center gap-4">
                  <Switch
                    checked={profileData.is_company}
                    onCheckedChange={(checked) => setProfileData(prev => ({ ...prev, is_company: checked }))}
                  />
                  <Label>Je suis une entreprise</Label>
                </div>

                {profileData.is_company && (
                  <div className="space-y-2">
                    <Label>Nom de l'entreprise</Label>
                    <Input
                      value={profileData.company_name}
                      onChange={(e) => setProfileData(prev => ({ ...prev, company_name: e.target.value }))}
                      placeholder="Nom de l'entreprise"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label>Informations fiscales (optionnel)</Label>
                  <Input
                    value={profileData.tax_info}
                    onChange={(e) => setProfileData(prev => ({ ...prev, tax_info: e.target.value }))}
                    placeholder="NIF, RC, etc."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Ville</Label>
                    <Input
                      value={profileData.city}
                      onChange={(e) => setProfileData(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="Ville"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Pays</Label>
                    <Input
                      value={profileData.country}
                      onChange={(e) => setProfileData(prev => ({ ...prev, country: e.target.value }))}
                      disabled
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Adresse</Label>
                  <Textarea
                    value={profileData.address}
                    onChange={(e) => setProfileData(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="Adresse complète"
                    rows={3}
                  />
                </div>

                <Button onClick={handleSaveProfile} className="w-full sm:w-auto">
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les modifications
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="account">
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle>Informations personnelles</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Prénom</Label>
                    <Input
                      value={userData.first_name}
                      onChange={(e) => setUserData(prev => ({ ...prev, first_name: e.target.value }))}
                      placeholder="Prénom"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nom</Label>
                    <Input
                      value={userData.last_name}
                      onChange={(e) => setUserData(prev => ({ ...prev, last_name: e.target.value }))}
                      placeholder="Nom"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={userData.email}
                    onChange={(e) => setUserData(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="email@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Téléphone</Label>
                  <Input
                    type="tel"
                    value={userData.phone_number}
                    onChange={(e) => setUserData(prev => ({ ...prev, phone_number: e.target.value }))}
                    placeholder="+224 XXX XX XX XX"
                  />
                </div>

                <Button onClick={handleSaveUser} className="w-full sm:w-auto">
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les modifications
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="preferences">
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle>Préférences</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Notifications</h3>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <Label>Notifications par email</Label>
                      </div>
                      <Switch
                        checked={preferences.notifications_email}
                        onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, notifications_email: checked }))}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Phone className="w-4 h-4 text-muted-foreground" />
                        <Label>Notifications par SMS</Label>
                      </div>
                      <Switch
                        checked={preferences.notifications_sms}
                        onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, notifications_sms: checked }))}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Bell className="w-4 h-4 text-muted-foreground" />
                        <Label>Notifications push</Label>
                      </div>
                      <Switch
                        checked={preferences.notifications_push}
                        onCheckedChange={(checked) => setPreferences(prev => ({ ...prev, notifications_push: checked }))}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Localisation</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Langue</Label>
                      <Select 
                        value={preferences.language} 
                        onValueChange={(value) => setPreferences(prev => ({ ...prev, language: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="fr">Français</SelectItem>
                          <SelectItem value="en">English</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label>Fuseau horaire</Label>
                      <Select 
                        value={preferences.timezone} 
                        onValueChange={(value) => setPreferences(prev => ({ ...prev, timezone: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Africa/Conakry">Africa/Conakry (GMT)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>

                <Button onClick={handleSavePreferences} className="w-full sm:w-auto">
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les préférences
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="security">
            <Card className="bg-card/50 border-border">
              <CardHeader>
                <CardTitle>Sécurité</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Changer le mot de passe</h3>
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Mot de passe actuel</Label>
                      <Input
                        type="password"
                        value={securityData.current_password}
                        onChange={(e) => setSecurityData(prev => ({ ...prev, current_password: e.target.value }))}
                        placeholder="Mot de passe actuel"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nouveau mot de passe</Label>
                      <Input
                        type="password"
                        value={securityData.new_password}
                        onChange={(e) => setSecurityData(prev => ({ ...prev, new_password: e.target.value }))}
                        placeholder="Nouveau mot de passe"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Confirmer le nouveau mot de passe</Label>
                      <Input
                        type="password"
                        value={securityData.confirm_password}
                        onChange={(e) => setSecurityData(prev => ({ ...prev, confirm_password: e.target.value }))}
                        placeholder="Confirmer le nouveau mot de passe"
                      />
                    </div>
                  </div>
                </div>

                <Button onClick={handleChangePassword} className="w-full sm:w-auto">
                  <Key className="w-4 h-4 mr-2" />
                  Changer le mot de passe
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </DashboardLayout>
    </>
  );
};

export default ClientSettings;

